import type { BuiltInTool, BuiltInToolError } from './types'

/**
 * How many times tasks may retry one chain of runs. A person's retry from Blockers is never counted or refused;
 * past this, a task escalates instead of starting another paid lap on the same failure.
 */
export const MAX_TASK_RETRIES = 3

const RUN_ID = { type: 'string', description: 'The run id, as `os.list_blockers` names it' } as const

const ESCALATE_HINT = 'Escalate it with os.escalate_run instead, naming what failed and what you would do next.'

/** The refusals every one of these tools shares: the run it names is not a stopped run someone can act on. */
const RUN_ERRORS: readonly BuiltInToolError[] = [
    { code: 'run_id_required', when: '`runId` is missing.', handling: 'tool_error' },
    { code: 'note_required', when: '`note` is missing or empty.', handling: 'tool_error' },
    { code: 'run_not_found', when: 'No run with that id in this enterprise.', handling: 'tool_error' },
    { code: 'own_run', when: 'The run named is the one calling.', handling: 'tool_error' },
    {
        code: 'not_stopped:<status>',
        when: 'The run is not on Blockers: it was cancelled, it finished on an outcome its task follows, or (to retry or clear) it is still active. A stalled or spend-paused run can only be escalated.',
        handling: 'tool_error',
    },
    {
        code: 'already_retried',
        when: 'A later run already retried or routed it. Act on that run instead.',
        handling: 'tool_error',
    },
    { code: 'already_cleared', when: 'Someone already cleared it.', handling: 'tool_error' },
]

/**
 * Tools a task uses to move the runs on Blockers: start one again, take one off, or hand one to the person who
 * can decide it. Only the OS can do any of these, because they act on runs. None of them decides a gate.
 */
export const OS_RUN_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.retry_run',
        family: 'os.runs',
        summary: 'Start a stopped run again, with the same input, so its work moves.',
        description: `Retry one stopped run: start a fresh run of the same task with the same input. Only a run on Blockers: failed, blocked, or finished on an outcome nothing follows, and not already cleared or retried. Pass runId and note (one line on why another run should get through, recorded on the run). Tasks may retry one chain ${MAX_TASK_RETRIES} times; past that, escalate it.`,
        inputSchema: {
            type: 'object',
            properties: {
                runId: RUN_ID,
                note: { type: 'string', description: 'Why another run should get through, in one line' },
            },
            required: ['runId', 'note'],
            additionalProperties: false,
        },
        returns:
            '`{ runId, retryRunId, status, taskRetries }`: the new run, `queued`, or `pending` while its task or member is busy, and how many times tasks have now retried the chain.',
        callers: ['run'],
        effect: 'write',
        sideEffects:
            "Starts a paid run of the stopped run's task. A blocked run is cancelled as it is retried, and the issue it works is marked in flight again.",
        retry: 'idempotent',
        retryNote:
            'Keyed on the tool call: the same call sent twice starts one run. A retried run is refused afterwards, so a later call starts nothing either.',
        errors: [
            ...RUN_ERRORS,
            {
                code: 'retry_limit_reached',
                when: `Tasks have already retried this chain ${MAX_TASK_RETRIES} times.`,
                handling: 'tool_error',
                hint: ESCALATE_HINT,
            },
            {
                code: 'task_not_runnable',
                when: "The run's task is deprecated, has no current version, or has nobody to run it. Escalate it instead.",
                handling: 'tool_error',
            },
        ],
        examples: [
            {
                input: {
                    runId: '5f0c1d2e-0000-4000-8000-000000000001',
                    note: 'Ran out of steps after pushing most of the change; the next run continues from the branch',
                },
            },
        ],
        public: true,
    },
    {
        name: 'os.route_run',
        family: 'os.runs',
        summary: "Send a stopped run's work to another task of its pipeline, instead of running the same task again.",
        description: `Route one stopped run: start a run of another task of its pipeline with the same input, in the same execution, when running the same task again would stop the same way. The task is one that already ran on the run's issue or pull request (send it back to an earlier stage), or one the stopped run's task names as a successor (move it on past this stage). Only a run on Blockers, as for os.retry_run. Pass runId, task (the slug to run next) and note (what that task should do, and why; the new run reads it as routedFrom in its input). Routes and retries share one limit: tasks may retry or route one chain ${MAX_TASK_RETRIES} times; past that, escalate it.`,
        inputSchema: {
            type: 'object',
            properties: {
                runId: RUN_ID,
                task: { type: 'string', description: 'The slug of the task to run next' },
                note: {
                    type: 'string',
                    description: 'What that task should do with it, and why, in a line or two',
                },
            },
            required: ['runId', 'task', 'note'],
            additionalProperties: false,
        },
        returns:
            '`{ runId, routedRunId, task, status, taskRetries }`: the new run, `queued`, or `pending` while its task or member is busy, and how many times tasks have now retried or routed the chain.',
        callers: ['run'],
        effect: 'write',
        sideEffects:
            'Starts a paid run of the task named. A blocked run is cancelled as it is routed, and the issue it works is marked in flight again.',
        retry: 'idempotent',
        retryNote:
            'Keyed on the tool call: the same call sent twice starts one run. A routed run is refused afterwards, so a later call starts nothing either.',
        errors: [
            ...RUN_ERRORS,
            { code: 'task_required', when: '`task` is missing.', handling: 'tool_error' },
            {
                code: 'same_task',
                when: "`task` is the stopped run's own task.",
                handling: 'tool_error',
                hint: 'Retry it with os.retry_run instead.',
            },
            {
                code: 'task_not_in_pipeline',
                when: "No run of that task is on the run's issue or pull request, and its task names no such successor.",
                handling: 'tool_error',
                hint: ESCALATE_HINT,
            },
            {
                code: 'retry_limit_reached',
                when: `Tasks have already retried or routed this chain ${MAX_TASK_RETRIES} times.`,
                handling: 'tool_error',
                hint: ESCALATE_HINT,
            },
            {
                code: 'task_not_runnable',
                when: 'The task named is deprecated, has no current version, or has nobody to run it. Escalate it instead.',
                handling: 'tool_error',
            },
        ],
        examples: [
            {
                input: {
                    runId: '5f0c1d2e-0000-4000-8000-000000000004',
                    task: 'write-red-suite',
                    note: 'The locked tests never cover the last acceptance criterion; add them, then implement runs on',
                },
            },
        ],
        public: true,
    },
    {
        name: 'os.clear_run',
        family: 'os.runs',
        summary: 'Take a stopped run off Blockers without running it again.',
        description:
            'Take one stopped run off Blockers without running it again, because nothing more is needed: its issue or pull request is closed or merged, or it is a leftover of a wait someone already ended. A failed run, or one that finished on an outcome nothing follows, is cleared; a blocked run is cancelled. Pass runId and note (why nothing more is needed, recorded on the run). The work item a stopped pipeline held goes back to its list once no later run exists.',
        inputSchema: {
            type: 'object',
            properties: {
                runId: RUN_ID,
                note: { type: 'string', description: 'Why nothing more is needed, in one line' },
            },
            required: ['runId', 'note'],
            additionalProperties: false,
        },
        returns: '`{ runId, status, cleared }`: the run as it now stands.',
        callers: ['run'],
        effect: 'write',
        sideEffects: 'Puts the work item the stopped pipeline held back on its list when no later run on it exists.',
        retry: 'idempotent',
        retryNote: 'The same call sent twice clears once. A cleared run is refused afterwards.',
        errors: RUN_ERRORS,
        examples: [
            {
                input: {
                    runId: '5f0c1d2e-0000-4000-8000-000000000002',
                    note: 'The pull request merged by hand on 28 Sep',
                },
            },
        ],
        public: true,
    },
    {
        name: 'os.escalate_run',
        family: 'os.runs',
        summary: 'Hand a stopped run to its team lead, or to the CEO, and record that you did.',
        description:
            "Hand one stopped run to someone who can decide it, and record it on the run so nobody hands it on twice. to: lead (the lead of the first of the running member's teams led by someone else, or the COO where there is none; they own the decision from then on) or ceo (record it before you put the question to the CEO with os.ask_ceo). Pass runId, to and note (what is stuck, what was tried, and the move you recommend). os.list_blockers shows the escalation on the row from then on.",
        inputSchema: {
            type: 'object',
            properties: {
                runId: RUN_ID,
                to: { type: 'string', enum: ['lead', 'ceo'] },
                note: {
                    type: 'string',
                    description: 'What is stuck, what was tried, and the move you recommend',
                },
            },
            required: ['runId', 'to', 'note'],
            additionalProperties: false,
        },
        returns:
            '`{ runId, to, memberId, memberName, alreadyEscalated }`: who now owns the decision. `alreadyEscalated` is true when the run was already escalated to them, and nothing new is recorded.',
        callers: ['run'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'Escalating a run again to the same person records nothing new.',
        errors: [
            ...RUN_ERRORS,
            { code: 'to_invalid', when: '`to` is not `lead` or `ceo`.', handling: 'tool_error' },
            {
                code: 'no_lead',
                when: "None of the running member's teams has a lead other than that member (or they sit on no team), and nobody holds the COO role to act as lead.",
                handling: 'tool_error',
                hint: 'Escalate it to ceo instead.',
            },
        ],
        examples: [
            {
                input: {
                    runId: '5f0c1d2e-0000-4000-8000-000000000003',
                    to: 'lead',
                    note: 'Loop cap on recover-red-suite (2/2): each lap found the same failing test outside the issue. Recommend closing the loop and filing the test separately.',
                },
            },
        ],
        public: true,
    },
]
